/**
 * Rendered vs Specification asset labelling — Patch 37.4.18.
 *
 * Historically the UI classified an asset by ``content_type`` alone,
 * which — after the media-engine image-render path added a v2 whose
 * ``content_type`` is inherited verbatim from the v1 prompt spec —
 * meant a rendered deliverable was still being labelled
 * "Image Prompt Social" and previewed as prompt text.
 *
 * The authoritative discriminator is ``payload_maturity`` (mirroring
 * ``app.orchestration.content_vault_service.is_final_deliverable`` on
 * the backend, which was already using this field correctly). This
 * helper centralises the label + classification rules so every
 * surface — list card, drawer title, review preview, AI Studio card
 * — shares one canonical decision.
 */
import type { GeneratedAsset } from "@/entities/ai-studio";

/**
 * Returns true when the asset is a rendered deliverable — i.e. the
 * paid provider call succeeded, media bytes were persisted, and the
 * lifecycle event should show media, not the prompt spec.
 *
 * Kept in strict lock-step with the backend's
 * ``is_final_deliverable`` semantics: ``payload_maturity`` is the
 * authoritative signal. ``media_file_ids`` presence is a defensive
 * belt-and-suspenders check for consumers that receive projections
 * without payload_maturity populated.
 */
export function isRenderedDeliverable(
  asset: Pick<GeneratedAsset,
    "payload_maturity"
    | "media_file_ids"
    | "is_head"
    | "superseded"
  >,
): boolean {
  if (asset.superseded) return false;
  if (asset.is_head === false) return false;
  if (asset.payload_maturity === "rendered_deliverable") {
    return true;
  }
  // Fallback for projections that haven't been re-baked with the
  // payload_maturity field yet — presence of a media file id AND
  // NOT being a superseded/non-head row is enough to treat as
  // rendered.
  return (asset.media_file_ids?.length ?? 0) > 0;
}

/**
 * Human-facing content-type label. Rendered deliverables collapse to
 * the media noun ("Image", "Video") regardless of the inherited
 * ``content_type``. Specifications keep their prettified label so the
 * founder can still tell an ``image_prompt_social`` from an
 * ``image_prompt_flyer``.
 *
 * NEVER returns ``"Image Prompt Social"`` for a rendered v2 — that
 * was the exact miscommunication that surfaced in the certification
 * bug report.
 */
export function getAssetLabel(
  asset: Pick<GeneratedAsset,
    "content_type"
    | "capability"
    | "payload_maturity"
    | "media_file_ids"
    | "is_head"
    | "superseded"
  >,
): string {
  if (isRenderedDeliverable(asset)) {
    // Rendered — collapse to the media noun so the customer never
    // sees "Image Prompt Social" on a fully-rendered asset.
    if (asset.capability === "image_generation") return "Image";
    if (asset.capability === "video_generation") return "Video";
    if (asset.capability === "voice_generation") return "Voice";
    // Unknown capability but a media file is attached — the safest
    // customer-facing label is still "Media" rather than the raw
    // spec content_type.
    return "Media";
  }
  return prettifyContentType(asset.content_type);
}

/**
 * Fallback text-only prettifier for specification content types.
 * Extracted so tests can lock the transformation independently.
 */
export function prettifyContentType(contentType: string | null | undefined): string {
  if (!contentType) return "Asset";
  return contentType.replace(/_/g, " ");
}
